import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainHeader from '../../shared/MainHeader/MainHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapPin, faArrowLeft, faPlus, faTimes, faSpinner, faPencilAlt,
  faTrash, faCompress, faSearchPlus, faSearchMinus,
  faCrosshairs, faExclamationTriangle, faLink,
  faSave, faEye, faLayerGroup, faInfoCircle, faCheck
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getAuthHeader } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import * as pdfjsLib from 'pdfjs-dist';
import './PinpointViewer.css';

// Configure PDF.js worker — served locally from public/ to avoid CDN round-trip.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const ASSET_TYPES = [
  { value: 'anchor_points', label: 'Anchor Points' },
  { value: 'cradles', label: 'Cradles' },
  { value: 'ladders', label: 'Ladders' },
  { value: 'walkways', label: 'Walkways' },
  { value: 'handrails', label: 'Handrails' },
  { value: 'static_lines', label: 'Static Lines' },
  { value: 'davit_bases', label: 'Abseil Systems' }
];

const CATEGORIES = [
  'Roof Safety', 'Fire Safety', 'Electrical',
  'Building Maintenance', 'Equipment Safety', 'General'
];

const PIN_COLORS = [
  { value: '#EF4444', label: 'Red' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#10B981', label: 'Green' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#6B7280', label: 'Grey' },
  { value: '#1F2937', label: 'Dark' }
];

const STATUS_OPTIONS = ['Active', 'Inactive', 'Needs Inspection', 'Failed', 'Pass'];

const getAssetLabel = (assetType) => ASSET_TYPES.find(a => a.value === assetType)?.label || assetType;

export default function PinpointViewer() {
  const { id: propertyId, pinpointId } = useParams();
  const navigate = useNavigate();

  const [pinpointDoc, setPinpointDoc] = useState(null);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docUrl, setDocUrl] = useState(null);
  const [docMime, setDocMime] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const [mode, setMode] = useState('view');
  const [selectedPin, setSelectedPin] = useState(null);
  const [hoveredPin, setHoveredPin] = useState(null);
  const [pendingPin, setPendingPin] = useState(null);

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const [showPinForm, setShowPinForm] = useState(false);
  const [pinForm, setPinForm] = useState({
    label: '', description: '', pin_color: '#EF4444',
    category: 'Roof Safety', linked_assets: [], status: 'Active'
  });
  const [savingPin, setSavingPin] = useState(false);
  const [editingPinId, setEditingPinId] = useState(null);
  const [assetPickerType, setAssetPickerType] = useState('');

  const [availableAssets, setAvailableAssets] = useState({});
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // docMime tracked for PDF conversion logic in fetchPinpointData

  const fetchPinpointData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/pinpoint/${pinpointId}`),
        { headers: getAuthHeader() }
      );
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setPinpointDoc(data.pinpointDocument);
      setPins(data.pins || []);
      setDocMime(data.pinpointDocument.mime_type);

      const imgRes = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/documents/${data.pinpointDocument.document_id}/download`),
        { headers: getAuthHeader() }
      );
      if (imgRes.ok) {
        const blob = await imgRes.blob();
        const mime = data.pinpointDocument.mime_type || blob.type;

        if (mime === 'application/pdf') {
          // Render PDF first page to a canvas, then convert to image URL
          try {
            const arrayBuffer = await blob.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer, isEvalSupported: false }).promise;
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 2.0 }); // High-res render
            const canvas = document.createElement('canvas');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext('2d');
            await page.render({ canvasContext: ctx, viewport }).promise;
            setDocUrl(canvas.toDataURL('image/png'));
          } catch (pdfErr) {
            console.error('PDF render error:', pdfErr);
            setDocUrl(URL.createObjectURL(blob));
          }
        } else {
          setDocUrl(URL.createObjectURL(blob));
        }
      }
    } catch (error) {
      console.error('Error loading pinpoint:', error);
      toast.error('Failed to load pinpoint drawing');
    } finally {
      setLoading(false);
    }
  }, [propertyId, pinpointId]);

  const fetchAssets = useCallback(async () => {
    try {
      setLoadingAssets(true);
      const res = await fetch(
        getApiEndpoint(`/api/properties/${propertyId}/pinpoint/${pinpointId}/available-assets`),
        { headers: getAuthHeader() }
      );
      if (res.ok) {
        const data = await res.json();
        setAvailableAssets(data.assets || {});
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoadingAssets(false);
    }
  }, [propertyId, pinpointId]);

  useEffect(() => {
    fetchPinpointData();
    fetchAssets();
    return () => { if (docUrl) URL.revokeObjectURL(docUrl); };
  }, [fetchPinpointData, fetchAssets]);

  const handleImageClick = (e) => {
    if (mode !== 'place') return;
    if (isPanning) return;
    const target = imageRef.current;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setPendingPin({ x_percent: x, y_percent: y });
    setPinForm({ label: '', description: '', pin_color: '#EF4444', category: 'Roof Safety', linked_assets: [], status: 'Active' });
    setEditingPinId(null);
    setAssetPickerType('');
    setShowPinForm(true);
  };

  const handleSavePin = async (e) => {
    e.preventDefault();
    if (!pinForm.label.trim()) { toast.error('Pin label is required'); return; }
    try {
      setSavingPin(true);
      const payload = { label: pinForm.label, description: pinForm.description, pin_color: pinForm.pin_color, category: pinForm.category, linked_assets: pinForm.linked_assets, status: pinForm.status };
      if (editingPinId) {
        const pin = pins.find(p => p.id === editingPinId);
        const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/pinpoint/${pinpointId}/pins/${editingPinId}`),
          { method: 'PUT', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ ...payload, x_percent: pin.x_percent, y_percent: pin.y_percent }) });
        if (!res.ok) throw new Error('Failed to update pin');
        const data = await res.json();
        setPins(prev => prev.map(p => p.id === editingPinId ? data.pin : p));
        toast.success('Pin updated');
      } else {
        const res = await fetch(getApiEndpoint(`/api/properties/${propertyId}/pinpoint/${pinpointId}/pins`),
          { method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ ...payload, x_percent: pendingPin.x_percent, y_percent: pendingPin.y_percent }) });
        if (!res.ok) throw new Error('Failed to create pin');
        const data = await res.json();
        setPins(prev => [...prev, data.pin]);
        toast.success('Pin placed');
      }
      setShowPinForm(false); setPendingPin(null); setEditingPinId(null); setMode('view');
    } catch (error) { console.error('Error saving pin:', error); toast.error('Failed to save pin'); }
    finally { setSavingPin(false); }
  };

  const handleDeletePin = async (pinId) => {
    if (!window.confirm('Delete this pin?')) return;
    try {
      await fetch(getApiEndpoint(`/api/properties/${propertyId}/pinpoint/${pinpointId}/pins/${pinId}`), { method: 'DELETE', headers: getAuthHeader() });
      setPins(prev => prev.filter(p => p.id !== pinId)); setSelectedPin(null); toast.success('Pin deleted');
    } catch { toast.error('Failed to delete pin'); }
  };

  const handleEditPin = (pin) => {
    setEditingPinId(pin.id);
    setPinForm({ label: pin.label, description: pin.description || '', pin_color: pin.pin_color || '#EF4444', category: pin.category || 'Roof Safety', linked_assets: pin.linked_assets || [], status: pin.status || 'Active' });
    setAssetPickerType(''); setShowPinForm(true); setSelectedPin(null);
  };

  const addAssetLink = (assetType, assetId) => {
    if (pinForm.linked_assets.some(a => a.asset_type === assetType && a.asset_id === assetId)) return;
    setPinForm(p => ({ ...p, linked_assets: [...p.linked_assets, { asset_type: assetType, asset_id: assetId }] }));
  };
  const removeAssetLink = (index) => setPinForm(p => ({ ...p, linked_assets: p.linked_assets.filter((_, i) => i !== index) }));
  const getAssetName = (assetType, assetId) => {
    const asset = (availableAssets[assetType] || []).find(a => a.id === parseInt(assetId));
    return asset ? (asset.name || `#${asset.id}`) : `#${assetId}`;
  };

  const zoomIn = () => setScale(s => Math.min(s * 1.3, 5));
  const zoomOut = () => setScale(s => Math.max(s / 1.3, 0.3));
  const resetView = () => { setScale(1); setOffset({ x: 0, y: 0 }); };
  const handleWheel = (e) => { e.preventDefault(); setScale(s => Math.max(0.3, Math.min(5, s * (e.deltaY > 0 ? 0.9 : 1.1)))); };
  const handleMouseDown = (e) => { if (mode === 'place') return; if (e.button === 1 || (e.button === 0 && e.altKey)) { setIsPanning(true); setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y }); e.preventDefault(); } };
  const handleMouseMove = (e) => { if (isPanning) setOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y }); };
  const handleMouseUp = () => setIsPanning(false);

  const getStatusColor = (status) => {
    switch (status) { case 'Pass': case 'Active': return '#16a34a'; case 'Failed': return '#dc2626'; case 'Needs Inspection': return '#d97706'; case 'Inactive': return '#6b7280'; default: return '#1A80E5'; }
  };

  if (loading) return (
    <div className="ppv-page"><MainHeader /><div className="ppv-loading"><FontAwesomeIcon icon={faSpinner} spin className="ppv-loading-icon" /><div>Loading pinpoint drawing...</div></div></div>
  );

  if (!pinpointDoc) return (
    <div className="ppv-page"><MainHeader /><div className="ppv-loading"><FontAwesomeIcon icon={faExclamationTriangle} className="ppv-error-icon" /><div>Pinpoint drawing not found</div><button className="ppv-back-btn" onClick={() => navigate(-1)}>Go Back</button></div></div>
  );

  return (
    <div className="ppv-page">
      <MainHeader />

      <div className="ppv-toolbar">
        <div className="ppv-toolbar-left">
          <button className="ppv-back-btn" onClick={() => navigate(-1)}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
          <div className="ppv-title-block">
            <h1 className="ppv-title"><FontAwesomeIcon icon={faMapPin} className="ppv-title-icon" />{pinpointDoc.name}</h1>
            <span className="ppv-category-badge">{pinpointDoc.category}</span>
            <span className="ppv-pin-count">{pins.length} pin{pins.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="ppv-toolbar-right">
          <div className="ppv-zoom-controls">
            <button onClick={zoomOut} title="Zoom out"><FontAwesomeIcon icon={faSearchMinus} /></button>
            <span className="ppv-zoom-label">{Math.round(scale * 100)}%</span>
            <button onClick={zoomIn} title="Zoom in"><FontAwesomeIcon icon={faSearchPlus} /></button>
            <button onClick={resetView} title="Reset view"><FontAwesomeIcon icon={faCompress} /></button>
          </div>
          <div className="ppv-mode-toggle">
            <button className={`ppv-mode-btn ${mode === 'view' ? 'active' : ''}`} onClick={() => { setMode('view'); setPendingPin(null); }}><FontAwesomeIcon icon={faEye} /> View</button>
            <button className={`ppv-mode-btn ppv-mode-place ${mode === 'place' ? 'active' : ''}`} onClick={() => setMode('place')}><FontAwesomeIcon icon={faCrosshairs} /> Place Pin</button>
          </div>
          <button className={`ppv-sidebar-toggle ${showSidebar ? 'active' : ''}`} onClick={() => setShowSidebar(!showSidebar)}><FontAwesomeIcon icon={faLayerGroup} /></button>
        </div>
      </div>

      <div className="ppv-content">
        <div className={`ppv-canvas-area ${mode === 'place' ? 'placing' : ''}`} ref={containerRef}
          onWheel={handleWheel} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
          {mode === 'place' && <div className="ppv-place-hint"><FontAwesomeIcon icon={faCrosshairs} /> Click on the drawing to place a pin</div>}

          <div className="ppv-image-wrapper" style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}>
            {docUrl && (
              <img ref={imageRef} src={docUrl} alt={pinpointDoc.name} className="ppv-drawing-image"
                onLoad={() => setImageLoaded(true)} onClick={handleImageClick} draggable={false} />
            )}
            {!docUrl && !loading && (
              <div className="ppv-no-doc"><FontAwesomeIcon icon={faExclamationTriangle} /><p>Document file could not be loaded</p></div>
            )}

            {imageLoaded && pins.map(pin => (
              <div key={pin.id}
                className={`ppv-pin ${selectedPin?.id === pin.id ? 'selected' : ''} ${hoveredPin?.id === pin.id ? 'hovered' : ''}`}
                style={{ left: `${pin.x_percent}%`, top: `${pin.y_percent}%`, '--pin-color': pin.pin_color || '#EF4444' }}
                onClick={(e) => { e.stopPropagation(); if (mode === 'view') setSelectedPin(selectedPin?.id === pin.id ? null : pin); }}
                onMouseEnter={() => setHoveredPin(pin)} onMouseLeave={() => setHoveredPin(null)}>
                <div className="ppv-pin-marker"><FontAwesomeIcon icon={faMapPin} /></div>
                {(hoveredPin?.id === pin.id || selectedPin?.id === pin.id) && (
                  <div className="ppv-pin-tooltip">
                    <div className="ppv-pin-tooltip-label">{pin.label}</div>
                    {(pin.linked_assets || []).length > 0 && <div className="ppv-pin-tooltip-asset"><FontAwesomeIcon icon={faLink} /> {pin.linked_assets.length} asset{pin.linked_assets.length !== 1 ? 's' : ''}</div>}
                    <div className="ppv-pin-tooltip-status" style={{ color: getStatusColor(pin.status) }}>{pin.status}</div>
                  </div>
                )}
              </div>
            ))}

            {pendingPin && (
              <div className="ppv-pin pending" style={{ left: `${pendingPin.x_percent}%`, top: `${pendingPin.y_percent}%`, '--pin-color': pinForm.pin_color }}>
                <div className="ppv-pin-marker pulse"><FontAwesomeIcon icon={faMapPin} /></div>
              </div>
            )}
          </div>
        </div>

        {/* Detail panel */}
        {selectedPin && !showPinForm && (
          <div className="ppv-side-panel">
            <div className="ppv-panel-header">
              <h3><span className="ppv-detail-color" style={{ background: selectedPin.pin_color || '#EF4444' }} />{selectedPin.label}</h3>
              <button className="ppv-panel-close" onClick={() => setSelectedPin(null)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="ppv-panel-body">
              <div className="ppv-field"><span className="ppv-field-label">Status</span><span className="ppv-field-value" style={{ color: getStatusColor(selectedPin.status) }}>{selectedPin.status}</span></div>
              <div className="ppv-field"><span className="ppv-field-label">Category</span><span className="ppv-field-value">{selectedPin.category}</span></div>
              {(selectedPin.linked_assets || []).length > 0 && (
                <div className="ppv-field">
                  <span className="ppv-field-label">Linked Assets ({selectedPin.linked_assets.length})</span>
                  <div className="ppv-linked-assets-list">
                    {selectedPin.linked_assets.map((a, i) => (
                      <div key={i} className="ppv-linked-asset-chip"><FontAwesomeIcon icon={faLink} /><span>{getAssetLabel(a.asset_type)}</span><span className="ppv-chip-id">{getAssetName(a.asset_type, a.asset_id)}</span></div>
                    ))}
                  </div>
                </div>
              )}
              {selectedPin.description && <div className="ppv-field"><span className="ppv-field-label">Description</span><p className="ppv-field-desc">{selectedPin.description}</p></div>}
              {selectedPin.created_by_name && <div className="ppv-field"><span className="ppv-field-label">Created by</span><span className="ppv-field-value">{selectedPin.created_by_name}</span></div>}
              {selectedPin.created_at && <div className="ppv-field"><span className="ppv-field-label">Created</span><span className="ppv-field-value">{new Date(selectedPin.created_at).toLocaleDateString()}</span></div>}
            </div>
            <div className="ppv-panel-footer">
              <button className="ppv-btn ppv-btn-edit" onClick={() => handleEditPin(selectedPin)}><FontAwesomeIcon icon={faPencilAlt} /> Edit</button>
              <button className="ppv-btn ppv-btn-delete" onClick={() => handleDeletePin(selectedPin.id)}><FontAwesomeIcon icon={faTrash} /> Delete</button>
            </div>
          </div>
        )}

        {/* Pin form */}
        {showPinForm && (
          <div className="ppv-side-panel">
            <div className="ppv-panel-header">
              <h3><FontAwesomeIcon icon={editingPinId ? faPencilAlt : faPlus} /> {editingPinId ? 'Edit Pin' : 'New Pin'}</h3>
              <button className="ppv-panel-close" onClick={() => { setShowPinForm(false); setPendingPin(null); setEditingPinId(null); }}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <form onSubmit={handleSavePin} className="ppv-panel-body ppv-form">
              <div className="ppv-form-group"><label>Label *</label><input type="text" required value={pinForm.label} onChange={e => setPinForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Anchor Point AP-01" autoFocus /></div>
              <div className="ppv-form-row">
                <div className="ppv-form-group"><label>Category</label><select value={pinForm.category} onChange={e => setPinForm(p => ({ ...p, category: e.target.value }))}>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                <div className="ppv-form-group"><label>Status</label><select value={pinForm.status} onChange={e => setPinForm(p => ({ ...p, status: e.target.value }))}>{STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              </div>
              <div className="ppv-form-group"><label>Pin Colour</label><div className="ppv-color-picker">{PIN_COLORS.map(c => (<button key={c.value} type="button" className={`ppv-color-swatch ${pinForm.pin_color === c.value ? 'selected' : ''}`} style={{ background: c.value }} onClick={() => setPinForm(p => ({ ...p, pin_color: c.value }))} title={c.label} />))}</div></div>

              <div className="ppv-form-group">
                <label><FontAwesomeIcon icon={faLink} /> Linked Assets ({pinForm.linked_assets.length})</label>
                {pinForm.linked_assets.length > 0 && (
                  <div className="ppv-asset-tags">{pinForm.linked_assets.map((a, i) => (
                    <div key={i} className="ppv-asset-tag"><span className="ppv-asset-tag-type">{getAssetLabel(a.asset_type)}</span><span>{getAssetName(a.asset_type, a.asset_id)}</span><button type="button" onClick={() => removeAssetLink(i)} className="ppv-asset-tag-x"><FontAwesomeIcon icon={faTimes} /></button></div>
                  ))}</div>
                )}
                <select value={assetPickerType} onChange={e => setAssetPickerType(e.target.value)}><option value="">+ Add asset link...</option>{ASSET_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}</select>
                {assetPickerType && (
                  <div className="ppv-asset-picker-list">
                    {loadingAssets ? <div className="ppv-inline-msg"><FontAwesomeIcon icon={faSpinner} spin /> Loading...</div>
                    : (availableAssets[assetPickerType] || []).length === 0 ? <div className="ppv-inline-msg">No {getAssetLabel(assetPickerType)} found</div>
                    : (availableAssets[assetPickerType] || []).map(asset => {
                      const linked = pinForm.linked_assets.some(a => a.asset_type === assetPickerType && a.asset_id === asset.id);
                      return (<button key={asset.id} type="button" className={`ppv-asset-pick ${linked ? 'linked' : ''}`} onClick={() => !linked && addAssetLink(assetPickerType, asset.id)}>
                        <span className="ppv-asset-pick-name">{asset.name || `#${asset.id}`}</span><span className="ppv-asset-pick-meta">{asset.type} — {asset.status}</span>{linked && <FontAwesomeIcon icon={faCheck} className="ppv-pick-check" />}
                      </button>);
                    })}
                  </div>
                )}
              </div>

              <div className="ppv-form-group"><label>Description</label><textarea value={pinForm.description} onChange={e => setPinForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional notes" rows="3" /></div>
              <div className="ppv-form-actions">
                <button type="button" className="ppv-btn ppv-btn-cancel" onClick={() => { setShowPinForm(false); setPendingPin(null); setEditingPinId(null); }}>Cancel</button>
                <button type="submit" className="ppv-btn ppv-btn-primary" disabled={savingPin}>{savingPin ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : <><FontAwesomeIcon icon={faSave} /> {editingPinId ? 'Update' : 'Save Pin'}</>}</button>
              </div>
            </form>
          </div>
        )}

        {/* Sidebar */}
        {showSidebar && !showPinForm && !selectedPin && (
          <div className="ppv-side-panel">
            <div className="ppv-panel-header"><h3><FontAwesomeIcon icon={faLayerGroup} /> Pins ({pins.length})</h3></div>
            <div className="ppv-panel-body ppv-pin-list">
              {pins.length === 0 ? (
                <div className="ppv-empty"><FontAwesomeIcon icon={faInfoCircle} /><p>No pins placed yet.</p><p>Switch to <strong>Place Pin</strong> mode and click on the drawing.</p></div>
              ) : pins.map(pin => (
                <div key={pin.id} className={`ppv-pin-row ${selectedPin?.id === pin.id ? 'active' : ''}`}
                  onClick={() => setSelectedPin(pin)} onMouseEnter={() => setHoveredPin(pin)} onMouseLeave={() => setHoveredPin(null)}>
                  <span className="ppv-pin-row-dot" style={{ background: pin.pin_color || '#EF4444' }} />
                  <div className="ppv-pin-row-info">
                    <div className="ppv-pin-row-label">{pin.label}</div>
                    <div className="ppv-pin-row-meta"><span style={{ color: getStatusColor(pin.status) }}>{pin.status}</span>{(pin.linked_assets || []).length > 0 && <><span className="ppv-dot" /><span>{pin.linked_assets.length} asset{pin.linked_assets.length !== 1 ? 's' : ''}</span></>}</div>
                  </div>
                  <div className="ppv-pin-row-actions">
                    <button onClick={(e) => { e.stopPropagation(); handleEditPin(pin); }}><FontAwesomeIcon icon={faPencilAlt} /></button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeletePin(pin.id); }}><FontAwesomeIcon icon={faTrash} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
